import { BaseSensorWebController } from '../../../base/controller/BaseSensorWebController';
import { BaseSensorWebComponent } from '../../../base/BaseSensorWebComponent';
import { templateAtlassianJiraConfluenceSensor } from '../view/template';
import { IListenerConfig } from '../../../../../framework/BaseWebComponentController';
import { SensorEvent, SensorType } from '../../../types/sensor-type';
import { AtlassianJiraConfluenceView } from '../view/AtlassianJiraConfluenceView';
import { AtlassianJiraConfluenceComponent } from '../component/AtlassianJiraConfluenceComponent';
import { GzToastNotificationWebComponent } from '../../../../../ui/gzToastNotification';
import { GzToast, ToastSeverity } from '../../../../../ui/gzToastNotification/type';
import { translateFn } from '../../../../../utils/translator.helper';
import { getRequestDefaultOptions } from '../../../../../utils/request.utils';
import { AtlassianJiraConfluenceSensorDetails } from '../types';
import { SensorEnabledStatus } from '../../../../common/types';
import { SensorConnectivityErrors } from '../../../constants';
import { ButtonEvents } from '../../../../../ui/gzButton/types';

export class AtlassianJiraConfluenceController extends BaseSensorWebController {
  public component: BaseSensorWebComponent;
  public view: AtlassianJiraConfluenceView;
  public viewConstructor: typeof AtlassianJiraConfluenceView;
  public listOfSensors: SensorType[] = [SensorType.ATLASSIAN_CONFLUENCE, SensorType.ATLASSIAN_JIRA];
  private gridView: any;

  constructor(component: AtlassianJiraConfluenceComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: AtlassianJiraConfluenceView,
      viewTemplate: templateAtlassianJiraConfluenceSensor,
    });
  }

  public isSensorEnabled(): boolean {
    return true;
  }

  computeEventListeners(): IListenerConfig[] {
    return super.computeEventListeners().concat([
      {
        element: this.view.dom.$productAdminApiToken,
        event: SensorEvent.change,
        callback: this.inputChanged,
      },
      {
        element: this.view.dom.$productAdminEmail,
        event: SensorEvent.change,
        callback: this.inputChanged,
      },
      {
        element: this.view.dom.$organizationSiteDomain,
        event: SensorEvent.change,
        callback: this.inputChanged,
      },
      {
        element: this.view.dom.$jiraCheckbox,
        event: SensorEvent.change,
        callback: this.jiraCheckboxChanged,
      },
      {
        element: this.view.dom.$confluenceCheckbox,
        event: SensorEvent.change,
        callback: this.confluenceCheckboxChanged,
      },
      {
        element: this.view.dom.$addToList,
        event: ButtonEvents.CLICK,
        callback: this.addToList,
      },
    ]);
  }

  public resetData(): void {
    this.view.dom.$organizationSiteDomain.resetField();
    this.view.dom.$productAdminApiToken.resetField();
    this.view.dom.$productAdminEmail.resetField();
  }

  private async addToList(): Promise<void> {
    this.view.dom.$addToList.setLoadingState(true);
    const existingData: Array<AtlassianJiraConfluenceSensorDetails> = this.gridView?.dataProvider.getData();
    const sensorsToAdd: AtlassianJiraConfluenceSensorDetails[] = [];
    let sensorsWithSameConfiguration = 0;
    for (const sensorType of this.listOfSensors) {
      const organizationSiteDomain = this.view.dom.$organizationSiteDomain.value as string;
      const productAdminEmail = this.view.dom.$productAdminEmail.value as string;
      const productAdminApiToken = this.view.dom.$productAdminApiToken.value as string;
      if (existingData.some(item => item.domain + item.type === organizationSiteDomain + sensorType)) {
        sensorsWithSameConfiguration++;
        continue;
      }

      sensorsToAdd.push({
        domain: organizationSiteDomain,
        admin_email: productAdminEmail,
        api_token: productAdminApiToken,
        type: sensorType,
        enabledStatus: SensorEnabledStatus.ENABLED,
        isAddedNow: true,
      });
    }

    if (sensorsWithSameConfiguration === this.listOfSensors.length) {
      this.showWarningToast(translateFn('Unable to add sensors. Integrations already exist.'));
      this.view.dom.$addToList.setLoadingState(false);
      return;
    } else if (sensorsToAdd.length !== 0 && sensorsWithSameConfiguration > 0) {
      this.showInformativeToast(translateFn('Unable to add both sensors. Integration already exists for one sensor and the provided domain.'));
    }
    if (sensorsToAdd.length === 0) {
      this.view.dom.$addToList.setLoadingState(false);
      return;
    }
    await this.checkConnectivityAndAddToGrid(sensorsToAdd);
  }

  private async checkConnectivityAndAddToGrid(sensorsToAdd: AtlassianJiraConfluenceSensorDetails[]) {
    const translations = this.view.getTranslations();
    try {
      const response = await this.dependencies.api.clients.SensorsIntegrations.testConnectivity(
        sensorsToAdd,
        getRequestDefaultOptions()
      );
      if (response['success']) {
        await this.view.dom.$atlassianJiraConfluenceGrid.controller?.addToList(sensorsToAdd);
        this.view.dom.$addToList.setLoadingState(false);
        this.view.resetFormFields();
        this.resetData();
        return;
      } else {
        if (response['data'].length === 0) {
          const errorMessage = this.view.translateSensorConnectivityErrorCodes(SensorConnectivityErrors.UNKNOWN_ERROR);
          this.showErrorToast(errorMessage);
          return;
        }

        response['data'].forEach(sensorError => {
          const errorCode = sensorError['errorCode']
            ? sensorError['errorCode']
            : SensorConnectivityErrors.UNKNOWN_ERROR;
          if (sensorError['errorDescription'] && !sensorError['errorDescription'].endsWith('.')) {
            sensorError['errorDescription'] = sensorError['errorDescription'] + '.';
          }
          const errorDescription =
            !sensorError['errorUri'] && sensorError['errorDescription'] ? sensorError['errorDescription'] + ' ' : '';
          const errorMessage =
            translations.sensorNames[sensorError['sensorType']] +
            ': ' +
            errorDescription +
            this.view.translateSensorConnectivityErrorCodes(errorCode);
          this.showErrorToast(errorMessage, sensorError['errorUri']);
        });
      }
    } catch (e) {
      const errorMessage = e?.message || this.translator.getGenericErrorMessage();
      this.showErrorToast(errorMessage);
    } finally {
      this.view.dom.$addToList.setLoadingState(false);
    }
  }

  public getDetails(): AtlassianJiraConfluenceSensorDetails[] {
    const sensors = this.gridView?.dataProvider.getData();
    return sensors.map(sensor => {
      const { enabledStatus, domain, api_token, admin_email, type, _id, isAddedNow } =
        sensor;
      const newSensor = { enabledStatus, domain, api_token, admin_email, type };
      if (!isAddedNow) {
        newSensor['_id'] = _id;
      }
      return newSensor;
    });
  }

  private jiraCheckboxChanged(ev: CustomEvent): void {
    ev.stopPropagation();
    if (ev.detail.checked === true && !this.listOfSensors.includes(SensorType.ATLASSIAN_JIRA)) {
      this.listOfSensors.push(SensorType.ATLASSIAN_JIRA);
    } else if (ev.detail.checked === false) {
      this.listOfSensors = this.listOfSensors.filter(sensor => sensor !== SensorType.ATLASSIAN_JIRA);
    }
  }

  private confluenceCheckboxChanged(ev: CustomEvent): void {
    ev.stopPropagation();
    if (ev.detail.checked === true && !this.listOfSensors.includes(SensorType.ATLASSIAN_CONFLUENCE)) {
      this.listOfSensors.push(SensorType.ATLASSIAN_CONFLUENCE);
    } else if (ev.detail.checked === false) {
      this.listOfSensors = this.listOfSensors.filter(sensor => sensor !== SensorType.ATLASSIAN_CONFLUENCE);
    }
  }

  public async render(): Promise<void> {
    await super.render();

    this.gridView = this.view.dom.$atlassianJiraConfluenceGrid.controller?.view.dom.$gridView;
  }

  private showWarningToast(message: string): void {
    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: message,
      severity: ToastSeverity.WARNING,
    });
  }

  private showInformativeToast(message: string): void {
    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: message,
      severity: ToastSeverity.INFORMATIVE,
    });
  }

  /**
   * @param errorMessage
   * @param viewMoreURI
   * @private
   */
  private showErrorToast(errorMessage: string, viewMoreURI: string | null = null) {
    const toastConfig: GzToast = {
      message: errorMessage,
      severity: ToastSeverity.ERROR,
      keepOpen: true,
    };

    if (viewMoreURI) {
      toastConfig.hasAction = true;
      toastConfig.actionButtonText = this.dependencies.translateFn('View more');
      toastConfig.showInlineButton = true;
      toastConfig.actionCallback = () => {
        return window.open(viewMoreURI, '_blank');
      };
    }

    GzToastNotificationWebComponent.buildToastWrapperElement(toastConfig, undefined, false);
  }

  async getDataForView(): Promise<any> {
    return this.component.getSensors();
  }
}
